import React from "react";
import moment from "moment-timezone";
import { UPDATE_TYPES, ACTIONS, USER_TYPES } from "./constants";
import { getWorkerShiftTypeLabel } from "../../appV2/redesign/utils/worker";
import {
  getAddedLogsForVerificationPreference,
  getUpdatedLogsForVerificationPreference,
  getRemovedLogsForVerificationPreference,
} from "./verificationPreferenceLogs/verificationPreferenceLogs";

export function logValue(value) {
  if (Array.isArray(value)) {
    return `${value.join(", ")}`.trim() || "[]";
  }

  if (typeof value === "object" && value !== null) {
    const res = Object.entries(value)
      .map(([key, val]) => `${key}: ${JSON.stringify(val)}`)
      .join(", ");
    return `${res}`.trim() || "{}";
  }

  return String(value).trim() || "(blank)";
}

const getLogsString = ({ log }) => {
  const blank = "(blank)";
  const { performedBy, updatedFields, updateType, createdAt, action } = log;
  const createdDate = moment(createdAt).format("YYYY-MM-DD HH:mm a");
  const format = "DD MMM YYYY";
  const logString = `${createdDate} - `;
  const { email: performedByEmail } = performedBy || { email: "Unknown user" };

  if (updateType === UPDATE_TYPES.general) {
    return updatedFields.map((updatedField) => {
      let { key, previousValue, newValue } = updatedField;
      if (key === "licensedStates") {
        let txt = "";
        if (previousValue?.length) {
          txt = `removed ${key} ${previousValue}`;
        }
        if (newValue?.length) {
          txt += `${txt ? " and " : ""} added ${key} ${newValue}`;
        }
        return (
          <p key={key}>
            {logString} {performedByEmail} {txt}
          </p>
        );
      }
      key = getWorkerShiftTypeLabel(key);
      previousValue = logValue(previousValue);
      newValue = logValue(newValue);

      if (action === ACTIONS.added) {
        return (
          <p key={key}>
            {logString} {performedByEmail} added {key} <b>{newValue}</b>
          </p>
        );
      }

      if (action === ACTIONS.removed) {
        return (
          <p key={key}>
            {logString} {performedByEmail} removed {key} <b>{previousValue}</b>
          </p>
        );
      }
      return (
        <p key={key}>
          {logString} {performedByEmail} updated {key} from <i>{previousValue}</i> to{" "}
          <b>{newValue}</b>
        </p>
      );
    });
  }

  if (updateType === UPDATE_TYPES.requirement) {
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { action, requirement } = log;
    const [updatedField] = updatedFields;
    const { key, previousValue, newValue } = updatedField || {};
    if (action === ACTIONS.added) {
      return (
        <p>
          {logString} {performedByEmail} added requirement <b>{requirement.name}</b>
        </p>
      );
    }

    if (action === ACTIONS.removed) {
      return (
        <p>
          {logString} {performedByEmail} removed requirement <b>{requirement.name}</b>
        </p>
      );
    }

    if (action === ACTIONS.updated) {
      const previousDate = moment(previousValue).format(format);
      const newDate = moment(newValue).format(format);
      return (
        <p>
          {logString} {performedByEmail} updated requirement {requirement.name}
          's {key} from <i>{previousDate}</i> to <b>{newDate}</b>
        </p>
      );
    }
  }

  if (updateType === UPDATE_TYPES.user) {
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { action, user } = log;
    if (action === ACTIONS.removed) {
      return (
        <p>
          {logString} {performedByEmail} removed {USER_TYPES[user.type]} <b>{user.email}</b>
        </p>
      );
    }

    if (action === ACTIONS.added) {
      return (
        <p>
          {logString} {performedByEmail} added {USER_TYPES[user.type]} with{" "}
          {updatedFields.map((fields) => {
            const { key, newValue } = fields;
            return (
              /* @ts-expect-error FIXME this wrong type */
              <info key={key}>
                {key} <b>{logValue(newValue)}</b> {/* @ts-expect-error FIXME this wrong type */}
              </info>
            );
          })}
        </p>
      );
    }

    if (action === ACTIONS.updated) {
      return (
        <p>
          {logString} {performedByEmail} updated {USER_TYPES[user.type]} {user.email}'s{" "}
          {updatedFields.map((fields) => {
            const { key, previousValue, newValue } = fields;
            return (
              /* @ts-expect-error FIXME this wrong type */
              <info key={key}>
                {key} from <i>{logValue(previousValue)}</i> to <b>{logValue(newValue)}</b>{" "}
                {/* @ts-expect-error FIXME this wrong type */}
              </info>
            );
          })}
        </p>
      );
    }
  }

  if (updateType === UPDATE_TYPES.doc) {
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { document, action } = log;
    if (action === ACTIONS.added) {
      return (
        <p>
          {logString} {performedByEmail} added document <b>{document.name}</b>
        </p>
      );
    }
    if (action === ACTIONS.removed) {
      return (
        <p>
          {logString} {performedByEmail} removed uploaded doc <b>{document.name}</b>
        </p>
      );
    }
    if (action === ACTIONS.updated) {
      return (
        <p>
          {logString} {performedByEmail} updated document {document.name}'s{" "}
          {updatedFields.map((fields) => {
            const { key, previousValue, newValue } = fields;
            if (key === "expiry") {
              const previousDate = moment(previousValue).format("DD MMM YYYY");
              const newDate = moment(newValue).format("DD MMM YYYY");
              return (
                /* @ts-expect-error FIXME this wrong type */
                <info key={key}>
                  {key} from <i>{previousDate}</i> to <b>{newDate}</b>
                  {/* @ts-expect-error FIXME this wrong type */}
                </info>
              );
            } else {
              return (
                /* @ts-expect-error FIXME this wrong type */
                <info key={key}>
                  {key} from <i>{logValue(previousValue) || blank}</i> to{" "}
                  <b>{logValue(newValue)}</b>
                  {/* @ts-expect-error FIXME this wrong type */}{" "}
                </info>
              );
            }
          })}
        </p>
      );
    }
  }

  if (updateType === UPDATE_TYPES.VERIFICATION_PREFERENCE) {
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { action } = log;
    if (action === ACTIONS.added) {
      return getAddedLogsForVerificationPreference(
        logString,
        performedByEmail,
        updatedFields,
        blank
      );
    }
    if (action === ACTIONS.removed) {
      return getRemovedLogsForVerificationPreference(logString, performedByEmail, updatedFields);
    }
    if (action === ACTIONS.updated) {
      return getUpdatedLogsForVerificationPreference(
        logString,
        performedByEmail,
        updatedFields,
        blank
      );
    }
  }
};

export { getLogsString };
